import React, { useState } from 'react';
import { Avatar, AvatarGroup, Box, Button, Chip, CircularProgress, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AssignJobOfferDialog } from './AssignJobOfferDialog';
import { getAssigneeDisplayInfo } from '../utils/assignee-utils';
import { Controller, useFormContext } from 'react-hook-form';

type JobOfferStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'EXPIRED' | 'UNPUBLISHED';

const STATUS_CONFIG: Record<JobOfferStatus, { label: string; color: 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info' }> = {
    DRAFT:       { label: 'Brouillon',   color: 'default' },
    ACTIVE:      { label: 'Active',      color: 'success' },
    INACTIVE:    { label: 'Inactive',    color: 'warning' },
    EXPIRED:     { label: 'Expirée',     color: 'error' },
    UNPUBLISHED: { label: 'Non publiée', color: 'info' },
};

interface JobOfferFloatingBarProps {
    loading?: boolean;
    status?: JobOfferStatus | string;
    proId?: string;
    isDirty?: boolean;
    profileCompletionRate?: number;
    onSave?: () => void;
    onPublish: () => void;
}

const JobOfferFloatingBar: React.FC<JobOfferFloatingBarProps> = ({
    loading,
    status,
    proId,
    isDirty,
    profileCompletionRate = 0,
    onSave,
    onPublish,
}) => {
    const theme = useTheme();
    const [dialogOpen, setDialogOpen] = useState(false);
    const {control} = useFormContext();

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: theme.palette.background.paper,
                borderRadius: '999px',
                boxShadow: theme.shadows[8],
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 3,
                py: 1.5,
                zIndex: theme.zIndex.snackbar,
                border: `1px solid ${(theme.palette as any).border?.main ?? theme.palette.divider}`,
            }}
        >
            <Typography variant="body2" color={theme.palette.text.secondary}>
                Statut :
            </Typography>
            {status ? (
                <Chip
                    size="small"
                    label={STATUS_CONFIG[status as JobOfferStatus]?.label ?? status}
                    color={STATUS_CONFIG[status as JobOfferStatus]?.color ?? 'default'}
                    sx={{ fontWeight: 500 }}
                />
            ) : (
                <Typography variant="body2" color={theme.palette.text.disabled}>—</Typography>
            )}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Controller
                control={control}
                name="assignees"
                render={({ field }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color={theme.palette.text.secondary}>
                            Assigné à :
                        </Typography>
                        <AvatarGroup
                            max={5}
                            onClick={() => !loading && setDialogOpen(true)}
                            sx={{
                                cursor: loading ? 'default' : 'pointer',
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                fontSize: 12,
                                '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 12 },
                            }}>
                            {field.value.map(assignee => {
                                const info = getAssigneeDisplayInfo(assignee);
                                return (
                                    <Tooltip
                                        key={assignee?._id}
                                        title={`${info.firstName} ${info.lastName} (${info.email})`}
                                        arrow
                                        placement="top">
                                        <Avatar
                                            src={info.photo}
                                            sx={{ width: 28, height: 28, fontSize: 12, bgcolor: info.color }}>
                                            {`${info.firstName[0] || 'U'}`.toUpperCase()}
                                        </Avatar>
                                    </Tooltip>
                                );
                            })}
                        </AvatarGroup>
                        <Tooltip title="Attribuer un membre" arrow placement="top">
                            <Avatar
                                component={IconButton}
                                onClick={e => { e.stopPropagation(); if (!loading) setDialogOpen(true); }}
                                disabled={loading}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    padding: 0,
                                    bgcolor: theme.palette.primary.main,
                                    '&:hover': { bgcolor: theme.palette.primary.dark },
                                }}>
                                <i className="fi-br-pencil" style={{ fontSize: 12 }} />
                            </Avatar>
                        </Tooltip>
                        <AssignJobOfferDialog
                            open={dialogOpen}
                            onClose={() => setDialogOpen(false)}
                            job={{ assignees: field.value, proId }}
                            onSelected={(values: any[]) => {
                                field.onChange(values);
                                setDialogOpen(false);
                            }}
                        />
                    </Box>
                )}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Button
                color={status === 'DRAFT' ? "secondary" : "primary"}
                onClick={onSave}
                disabled={loading || !isDirty}
                sx={{ borderRadius: '999px', px: 3, textTransform: 'none' }}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
                Enregistrer
            </Button>

            {status === 'DRAFT' && (
                <Tooltip
                    title={profileCompletionRate < 100 ? `Complétez l'offre à 100% pour la publier (${profileCompletionRate}% complété)` : ''}
                    arrow
                    placement="top"
                >
                    <span>
                        <Button
                            variant="contained"
                            onClick={onPublish}
                            disabled={loading || profileCompletionRate < 100}
                            sx={{ borderRadius: '999px', px: 3, textTransform: 'none' }}
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            Publier l'offre
                        </Button>
                    </span>
                </Tooltip>
            )}
        </Box>
    );
};

export default JobOfferFloatingBar;
